import { useCallback } from 'react';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useUpdateAssemblyTableMutation } from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';

export const useEditComponentTable = () => {
  const structureId = useGetStructureId();
  const [updateAssemblyTable] = useUpdateAssemblyTableMutation();
  const setInsightsAssemblies = useSetAtom(state.allAssembliesForInsights);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const saveSingleEdit = useCallback(
    async (itemId: string, columnKey: string, value: unknown): Promise<void> => {
      const templateFieldData: Record<string, Record<string, unknown>> = {};
      templateFieldData[itemId] = { [columnKey]: value };

      try {
        await updateAssemblyTable({
          variables: {
            input: {
              structureId,
              assemblyIds: [itemId],
              assembly: { templateFieldData },
              isTemplateData: true,
            },
          },
        });

        setInsightsAssemblies((prev = []) => {
          const list = Array.isArray(prev) ? [...prev] : [];
          const idx = list.findIndex((a) => !!a && a.id === itemId);
          if (idx === -1) return prev;

          const assembly = list[idx];
          const existingTemplate =
            assembly && assembly.templateFieldData && typeof assembly.templateFieldData === 'object'
              ? assembly.templateFieldData
              : {};

          const newTemplate = { ...existingTemplate, [columnKey]: value };
          const updated = { ...assembly, templateFieldData: newTemplate };

          list[idx] = updated;
          return list;
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to update assembly';
        showSnackBar(message, 'error');
      }
    },
    [updateAssemblyTable, structureId, setInsightsAssemblies, showSnackBar]
  );

  const saveBulkEdit = useCallback(
    async (columnKey: string, value: unknown, itemIds: string[]): Promise<void> => {
      const templateFieldData: Record<string, Record<string, unknown>> = {};
      for (const id of itemIds) {
        templateFieldData[id] = { [columnKey]: value };
      }

      try {
        await updateAssemblyTable({
          variables: {
            input: {
              structureId,
              assemblyIds: itemIds,
              assembly: { templateFieldData },
              isTemplateData: true,
            },
          },
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to update assemblies';
        showSnackBar(message, 'error');
      }
    },
    [structureId, updateAssemblyTable, showSnackBar]
  );

  return { saveSingleEdit, saveBulkEdit };
};
